import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouteService } from '../../../../core/services/route.service';
import { map } from 'rxjs/operators';
import { AlertService, AuthService } from '@wm-libs/core';
import { StorageService } from '@wm-libs/core';
import { UserService } from '@wm-libs/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { NgClass, AsyncPipe } from '@angular/common';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-sign-in',
  templateUrl: './sign-in.component.html',
  styleUrls: ['../../styles/auth.scss'],
  standalone: true,
  imports: [NgClass, MatCardModule, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule, MatCheckboxModule, RouterLink, AsyncPipe, TranslateModule],
})
export class SignInComponent implements OnDestroy {
  private needTfa$: Subscription;
  signInForm: FormGroup;
  hidePassword = true;

  isMobile$ = this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small]).pipe(map((result) => result.matches));

  activationCode!: string;
  redirectToMms!: boolean;
  redirectToPool!: boolean;

  env = environment;
  isAccountActivated = false;

  constructor(
    private authService: AuthService,
    private fb: FormBuilder,
    private breakpointObserver: BreakpointObserver,
    private userService: UserService,
    private alertService: AlertService,
    private storageService: StorageService,
    private routeService: RouteService,
    private route: ActivatedRoute,
    private router: Router,
    private translateService: TranslateService
  ) {
    this.needTfa$ = this.userService.needTfa$.subscribe((needTfa) => {
      if (needTfa) {
        this.routeService.goToVerifyTfa();
      }
    });

    this.signInForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
      rememberMe: [false],
    });

    this.route.params.subscribe((params) => {
      this.activationCode = params['activationCode'];

      if (this.activationCode) {
        this.confirmAccount();
      }
    });

    this.route.queryParams.subscribe((params) => {
      this.redirectToMms = params['redirectToMms'];
      this.redirectToPool = params['redirectToPool'];
    });
  }

  get formControls() {
    return this.signInForm.controls;
  }

  signIn() {
    if (this.signInForm.valid) {
      const { email, password, rememberMe } = this.signInForm.value;

      this.authService.signIn(email, password, rememberMe).subscribe({
        next: (res) => {
          this.storageService.saveToken(res.token, res.tokenMms, rememberMe);

          if (res.data === null) {
            this.userService.setNeedTfa(true);
            this.routeService.goToVerifyTfa();
          } else {
            this.userService.setAuthenticated(true);
            this.userService.setNeedTfa(false);

            this.userService.setUserData(res.data);

            if (this.redirectToMms) {
              window.location.href = this.env.techMmsDomain;
            } else if (this.redirectToPool) {
              window.location.href = this.env.techPoolDomain;
            } else {
              this.routeService.goToMain();
            }
          }
        },
        error: (err) => {
          if (err.errorCode === 'USER_INACTIVE') {
            this.router.navigate(['/auth/after-signup'], {
              queryParams: { email: email },
            });
          }
        },
      });
    } else {
      this.alertService.showAlert(this.translateService.instant('FORMS.NOT_VALID'), 'error');
    }
  }

  confirmAccount(): void {
    this.authService.confirmAccount(this.activationCode).subscribe(() => {
      this.isAccountActivated = true;
      this.alertService.showAlert(this.translateService.instant('USER.ACCOUNT.ACTIVATED'), 'success');
    });
  }

  ngOnDestroy(): void {
    if (this.needTfa$) this.needTfa$.unsubscribe();
  }
}
